import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["message"],
  _excluded2 = ["message"];
import { Realtime } from 'ably';
import enviro from 'enviro';
import { sendMessageRpc, sendMessagesRpc, sendToolResponseRpc, sendToolApprovalRpc, cancelTurnRpc, steerTurnRpc, submitFeedbackRpc, getAblyTokenRpc, getOrCreateAblyChannelRpc, getTurnResultRpc } from '../../../__generated__/chirp/com/hubspot/breeze/engine/rpc/BreezeEngineService';
import { createDefaultChirpClient } from '../chirpClient';
import { createChatLogger } from '../logger';
import { Metrics } from '../chatMetrics';
import { isArborHost } from '../isArborHost';
import { fetchLocalAblyToken, getLocalAgentExecution, prepareAndEnqueueLocalSendMessage, prepareAndEnqueueLocalSendMessages, prepareAndEnqueueLocalToolResponse, prepareAndEnqueueLocalToolApproval } from '../localAgent';
let defaultPollingClient = null;
function getDefaultPollingClient() {
  if (!defaultPollingClient) {
    defaultPollingClient = createDefaultChirpClient();
  }
  return defaultPollingClient;
}
export function createAblyBreezeConnection(options = {}) {
  var _options$pollingChirp, _options$logger, _ref;
  const pollingClient = (_options$pollingChirp = options.pollingChirpClient) !== null && _options$pollingChirp !== void 0 ? _options$pollingChirp : getDefaultPollingClient();
  const logger = (_options$logger = options.logger) !== null && _options$logger !== void 0 ? _options$logger : createChatLogger();
  let activeHandler = null;
  let activeChannel = null;
  let activeChannelId = null;
  let currentConversationId = null;
  let activeLocalAgentId = null;
  let needsClientReset = false;
  let preconnectPromise = null;
  const ownsAblyClient = options.ablyClient == null;
  const canResetClient = !options.ablyClient || !!options.ablyClientFactory;
  const clientFactory = options.ablyClientFactory ? () => options.ablyClientFactory(handleAuthFailure) : () => createAblyRealtimeClient(createGetTokenFn(pollingClient, () => currentConversationId, {
    getAblyAuthToken: options.getAblyAuthToken,
    getIsLocalAgent: () => activeLocalAgentId !== null,
    getLocalAgentId: () => activeLocalAgentId,
    getChannelId: () => activeChannelId
  }), handleAuthFailure);
  let ablyClient = (_ref = options.ablyClient) !== null && _ref !== void 0 ? _ref : clientFactory();
  function attachSuspendedListener(client) {
    client.connection.on('suspended', stateChange => {
      var _activeHandler;
      (_activeHandler = activeHandler) === null || _activeHandler === void 0 || _activeHandler.onError({
        type: 'TRANSPORT_ERROR',
        message: 'Ably connection suspended',
        cause: stateChange
      });
    });
  }
  attachSuspendedListener(ablyClient);
  let connectingPromise = null;
  let connectingReject = null;
  let isLocalConversation = false;
  function resetClient() {
    if (!isArborHost()) {
      Metrics.counter('ably-connection-reset', {
        mode: 'ably'
      }).increment();
    }
    if (ownsAblyClient) {
      ablyClient.close();
    }
    ablyClient = clientFactory();
    attachSuspendedListener(ablyClient);
    connectingPromise = null;
  }
  function handleAblyMessage(message) {
    var _options$onAblyChanne, _activeChannelId;
    (_options$onAblyChanne = options.onAblyChannelEvent) === null || _options$onAblyChanne === void 0 || _options$onAblyChanne.call(options, message.name, message.data);
    logger.ablyMessage((_activeChannelId = activeChannelId) !== null && _activeChannelId !== void 0 ? _activeChannelId : 'unknown', activeHandler != null);
    if (!activeHandler) {
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(message.data);
    } catch (parseError) {
      activeHandler.onError({
        type: 'TRANSPORT_ERROR',
        message: `Failed to parse Ably message: ${String(parseError)}`,
        cause: parseError
      });
      return;
    }
    activeHandler.onEvent(parsed);
  }

  // Invoked when Ably token refresh has permanently failed (e.g. the
  // conversation was deleted). Surfaces the failure to the active handler, tears
  // down the subscription, and closes the owned client so Ably stops re-firing
  // authCallback forever.
  function handleAuthFailure() {
    var _activeHandler2;
    if (!isArborHost()) {
      Metrics.counter('ably-auth-failure', {
        mode: 'ably'
      }).increment();
    }
    if (connectingReject) {
      const reject = connectingReject;
      connectingReject = null;
      connectingPromise = null;
      reject(new Error('Ably authentication permanently failed'));
    }
    (_activeHandler2 = activeHandler) === null || _activeHandler2 === void 0 || _activeHandler2.onError({
      type: 'AUTH_FAILURE_PERMANENT',
      message: 'Ably authentication permanently failed; the conversation may no longer exist.'
    });
    unsubscribeInternal();
    if (ownsAblyClient) {
      ablyClient.close();
    }
  }

  // Unsubscribes and detaches the active channel. Nulls activeChannel before
  // issuing detach so re-entrant teardown calls cannot detach the same channel
  // twice. detach() returns Promise<void> in ably v3 and rejects when the
  // channel is torn down against a connection whose identity has already
  // shifted (e.g. "mismatched application id for existing connection"). The
  // channel is dead by that point so we swallow the rejection, but keep a
  // debug log and metric counter for observability (issue #4777).
  function teardownActiveChannel() {
    if (!activeChannel) {
      return;
    }
    const channel = activeChannel;
    activeChannel = null;
    channel.unsubscribe();
    void Promise.resolve(channel.detach()).catch(error => {
      logger.ablyDetachFailed(formatAblyError(error));
      if (!isArborHost()) {
        Metrics.counter('ably-detach-failed', {
          mode: 'ably'
        }).increment();
      }
    });
  }
  function unsubscribeInternal(markForReset = true) {
    teardownActiveChannel();
    activeHandler = null;
    activeChannelId = null;
    preconnectPromise = null;
    if (markForReset) {
      needsClientReset = true;
    }
  }
  function ensureConnected() {
    if (needsClientReset && canResetClient) {
      needsClientReset = false;
      resetClient();
    }
    if (ablyClient.connection.state === 'connected') {
      logger.ablyEnsureConnected('connected');
      return Promise.resolve();
    }
    if (connectingPromise) {
      logger.ablyEnsureConnected('already-connecting');
      return connectingPromise;
    }
    logger.ablyEnsureConnected(ablyClient.connection.state);
    ablyClient.connection.connect();
    const listeners = {
      onConnected: null,
      onFailed: null
    };
    connectingPromise = new Promise((resolve, reject) => {
      connectingReject = reject;
      listeners.onConnected = () => {
        if (listeners.onFailed) {
          ablyClient.connection.off('failed', listeners.onFailed);
        }
        connectingPromise = null;
        connectingReject = null;
        logger.ablyConnected();
        resolve();
      };
      listeners.onFailed = stateChange => {
        if (listeners.onConnected) {
          ablyClient.connection.off('connected', listeners.onConnected);
        }
        connectingPromise = null;
        connectingReject = null;
        logger.ablyConnectFailed(formatAblyStateChange(stateChange));
        if (!isArborHost()) {
          Metrics.counter('ably-connect-failed', {
            mode: 'ably'
          }).increment();
        }
        reject(Object.assign(new Error('Ably connection failed'), {
          cause: stateChange
        }));
      };
      ablyClient.connection.once('connected', listeners.onConnected);
      ablyClient.connection.once('failed', listeners.onFailed);
    });
    return connectingPromise;
  }
  function subscribeToChannel(channelId, handler) {
    teardownActiveChannel();
    activeHandler = handler;
    activeChannelId = channelId;
    preconnectPromise = null;
    activeChannel = ablyClient.channels.get(channelId, {
      params: {
        rewind: '100'
      }
    });
    activeChannel.subscribe(handleAblyMessage);
    logger.ablySubscribed(channelId);
    for (const state of ['attached', 'attaching', 'detached', 'suspended']) {
      activeChannel.on(state, () => {
        logger.ablyMessage(`channel:${state}`, true);
      });
    }
    activeChannel.on('failed', stateChange => {
      if (!activeHandler) {
        unsubscribeInternal();
        return;
      }
      const savedHandler = activeHandler;
      const savedChannelId = activeChannelId;
      unsubscribeInternal();
      if (ablyClient.connection.state === 'connected' && savedChannelId) {
        activeHandler = savedHandler;
        activeChannelId = savedChannelId;
        activeChannel = ablyClient.channels.get(savedChannelId, {
          params: {
            rewind: '100'
          }
        });
        activeChannel.subscribe(handleAblyMessage);
        activeChannel.on('failed', () => {
          savedHandler.onError({
            type: 'TRANSPORT_ERROR',
            message: 'Ably channel recovery failed',
            cause: stateChange
          });
          unsubscribeInternal();
        });
      } else {
        savedHandler.onError({
          type: 'TRANSPORT_ERROR',
          message: 'Ably channel entered failed state',
          cause: stateChange
        });
      }
    });
  }
  return {
    async send(request, handler, onMetadata, onChannelReady) {
      const localExecution = await getLocalAgentExecution(request.agentId);
      if (localExecution) {
        // Local-agent path: prepare-and-enqueue via the right RPC pair, then
        // optionally subscribe to whatever channel the prepare RPC returned.
        let response;
        if (typeof request.message === 'string') {
          response = await prepareAndEnqueueLocalSendMessage(pollingClient, request);
        } else {
          const {
              message: contentBlocks
            } = request,
            rest = _objectWithoutPropertiesLoose(request, _excluded);
          response = await prepareAndEnqueueLocalSendMessages(pollingClient, Object.assign({}, rest, {
            contentBlocks
          }));
        }
        currentConversationId = response.conversationId;
        isLocalConversation = true;
        activeLocalAgentId = localExecution === 'custom' ? request.agentId : null;
        onMetadata === null || onMetadata === void 0 || onMetadata(response.conversationId, response.turnId);
        if (response.ablyChannelId) {
          // Set channelId before ensureConnected so the auth callback can
          // mint a token scoped to the correct channel via the local server.
          activeChannelId = response.ablyChannelId;
          await ensureConnected();
          subscribeToChannel(response.ablyChannelId, handler);
        }
        return {
          conversationId: response.conversationId,
          turnId: response.turnId
        };
      }
      activeLocalAgentId = null;
      isLocalConversation = false;
      if (preconnectPromise) {
        try {
          await preconnectPromise;
        } catch (_unused) {
          // Preconnect failed; send() will set up the channel independently.
        }
      }
      // For existing conversations, subscribe BEFORE sending so we
      // don't miss events published while the RPC is in flight.
      let initialTitle;
      if (request.conversationId && !activeChannel) {
        currentConversationId = request.conversationId;
        // getOrCreateAblyChannelRpc runs first (plain CHIRP, no Ably auth) so
        // the channel exists on the backend before ensureConnected() fires the
        // Ably auth callback. Without this ordering, switching to ably mode on a
        // conversation started with streaming/polling fails because the token
        // endpoint rejects requests for channels that haven't been provisioned.
        const channelResponse = await pollingClient.call(getOrCreateAblyChannelRpc, {
          request: {
            conversationId: request.conversationId,
            historyLimit: 0
          }
        });
        initialTitle = channelResponse.title;
        await ensureConnected();
        subscribeToChannel(channelResponse.ablyChannelId, handler);
      } else if (activeChannel) {
        activeHandler = handler;
        // Preconnect already consumed rewind events via its no-op handler,
        // so no stale events will arrive on this channel. Signal the
        // transport that it's safe to process events immediately rather
        // than waiting for the sendMessage RPC to return.
        onChannelReady === null || onChannelReady === void 0 || onChannelReady();
      }

      // Remote path: pick the singular vs plural RPC based on message shape.
      // Above this point the request shape is uniform.
      let response;
      if (typeof request.message === 'string') {
        response = await pollingClient.call(sendMessageRpc, {
          request: request
        });
      } else {
        const {
            message: contentBlocks
          } = request,
          common = _objectWithoutPropertiesLoose(request, _excluded2);
        response = await pollingClient.call(sendMessagesRpc, {
          request: Object.assign({}, common, {
            contentBlocks
          })
        });
      }
      currentConversationId = response.conversationId;
      onMetadata === null || onMetadata === void 0 || onMetadata(response.conversationId, response.turnId, initialTitle);

      // Subscribe (or re-subscribe) when needed:
      // - No channel yet: new conversation, subscribe now with rewind to catch
      //   any events published before we attached.
      // - Channel mismatch: preconnect was skipped (e.g. a turn was in-flight),
      //   so activeChannel is stale. Re-subscribe to the correct channel.
      if (response.ablyChannelId && (!activeChannel || response.ablyChannelId !== activeChannelId)) {
        await ensureConnected();
        subscribeToChannel(response.ablyChannelId, handler);
      }
      logger.ablyConnectionSend(response.conversationId, response.ablyChannelId);
      return {
        conversationId: response.conversationId,
        turnId: response.turnId
      };
    },
    async sendToolResponse(request, handler) {
      activeHandler = handler;
      if (isLocalConversation) {
        const response = await prepareAndEnqueueLocalToolResponse(pollingClient, request);
        if (response.ablyChannelId) {
          await ensureConnected();
          subscribeToChannel(response.ablyChannelId, handler);
        }
        return;
      }
      if (request.conversationId && !activeChannel) {
        currentConversationId = request.conversationId;
        const channelResponse = await pollingClient.call(getOrCreateAblyChannelRpc, {
          request: {
            conversationId: request.conversationId,
            historyLimit: 0
          }
        });
        await ensureConnected();
        subscribeToChannel(channelResponse.ablyChannelId, handler);
      }
      await pollingClient.call(sendToolResponseRpc, {
        request
      });
    },
    async sendToolApproval(request, handler) {
      activeHandler = handler;
      if (isLocalConversation) {
        const response = await prepareAndEnqueueLocalToolApproval(pollingClient, request);
        if (response.ablyChannelId) {
          await ensureConnected();
          subscribeToChannel(response.ablyChannelId, handler);
        }
        return;
      }
      if (request.conversationId && !activeChannel) {
        currentConversationId = request.conversationId;
        const channelResponse = await pollingClient.call(getOrCreateAblyChannelRpc, {
          request: {
            conversationId: request.conversationId,
            historyLimit: 0
          }
        });
        await ensureConnected();
        subscribeToChannel(channelResponse.ablyChannelId, handler);
      }
      await pollingClient.call(sendToolApprovalRpc, {
        request
      });
    },
    async getOrCreateAblyChannel(conversationId, handler) {
      currentConversationId = conversationId;
      const response = await pollingClient.call(getOrCreateAblyChannelRpc, {
        request: {
          conversationId,
          historyLimit: 1
        }
      });
      await ensureConnected();
      subscribeToChannel(response.ablyChannelId, handler);
      return response;
    },
    async preconnect(conversationId) {
      if (activeChannel && currentConversationId === conversationId) {
        return;
      }
      const doPreconnect = async () => {
        currentConversationId = conversationId;
        const channelResponse = await pollingClient.call(getOrCreateAblyChannelRpc, {
          request: {
            conversationId,
            historyLimit: 0
          }
        });
        await ensureConnected();
        // Re-check: send() may have set up the channel while we awaited.
        // Avoid detaching a channel that send() is actively using.
        if (activeChannel && activeChannelId === channelResponse.ablyChannelId) {
          return;
        }
        // Subscribe with a no-op handler — the real handler is swapped in by
        // send() via the `else if (activeChannel) { activeHandler = handler }`
        // branch, which fires before sendMessageRpc is called.
        subscribeToChannel(channelResponse.ablyChannelId, {
          onEvent: () => {},
          onError: () => {}
        });
      };
      preconnectPromise = doPreconnect().finally(() => {
        preconnectPromise = null;
      });
      await preconnectPromise;
    },
    async cancelTurn(request) {
      await pollingClient.call(cancelTurnRpc, {
        request
      });
    },
    async steerTurn(request) {
      return await pollingClient.call(steerTurnRpc, {
        request
      });
    },
    async submitFeedback(request) {
      await pollingClient.call(submitFeedbackRpc, {
        request
      });
    },
    async getTurnResult(request) {
      return await pollingClient.call(getTurnResultRpc, {
        request
      });
    },
    subscribe(channelId, handler) {
      subscribeToChannel(channelId, handler);
    },
    unsubscribe() {
      unsubscribeInternal();
    },
    destroy() {
      unsubscribeInternal();
      if (ownsAblyClient) {
        ablyClient.close();
      }
    }
  };
}
function formatAblyError(error) {
  if (error == null) return 'unknown';
  if (error instanceof Error) return error.message;
  const e = error;
  if (typeof e.message === 'string') return e.message;
  return String(error);
}
function formatAblyStateChange(stateChange) {
  if (stateChange == null) return 'unknown';
  const sc = stateChange;
  const reason = sc.reason;
  if (reason) {
    var _reason$code, _reason$statusCode, _reason$message;
    return `code=${(_reason$code = reason.code) !== null && _reason$code !== void 0 ? _reason$code : '?'} statusCode=${(_reason$statusCode = reason.statusCode) !== null && _reason$statusCode !== void 0 ? _reason$statusCode : '?'} message=${(_reason$message = reason.message) !== null && _reason$message !== void 0 ? _reason$message : '?'}`;
  }
  if (sc.current !== undefined) {
    return `current=${String(sc.current)} previous=${String(sc.previous)}`;
  }
  return 'unknown';
}
function getAblyClientOptions() {
  const hublet = enviro.getHublet();
  return {
    environment: `hubspot-${hublet}`,
    fallbackHosts: ['a', 'b', 'c', 'd', 'e'].map(suffix => `hubspot-${hublet}-${suffix}-fallback.ably-realtime.com`)
  };
}
// Extracted so the auth-failure handling is unit-testable without constructing
// a real Ably Realtime client.
export function createAblyAuthCallback(getToken, onAuthFailure) {
  return (_tokenParams, callback) => {
    void getToken().then(response => callback(null, response.token), error => {
      // Handing a permanent failure to Ably makes it re-fire authCallback
      // forever. Surface it out-of-band so the connection can tear down,
      // and do NOT call Ably's callback — that stops the retry loop.
      if (isPermanentAuthFailure(error)) {
        onAuthFailure === null || onAuthFailure === void 0 || onAuthFailure();
        return;
      }
      callback(error instanceof Error ? error.message : 'Token refresh failed', null);
    });
  };
}
export function createAblyRealtimeClient(getToken, onAuthFailure) {
  return new Realtime(Object.assign({}, getAblyClientOptions(), {
    authCallback: createAblyAuthCallback(getToken, onAuthFailure),
    autoConnect: false,
    echoMessages: false
  }));
}
const TOKEN_REFRESH_MAX_CONSECUTIVE_FAILURES = 3;

// Thrown when token refresh has failed for the same conversation too many times
// in a row (e.g. the conversation was deleted). The Ably auth wiring detects
// this to stop Ably from re-firing authCallback forever.
export class PermanentAuthFailureError extends Error {
  constructor(conversationId) {
    super(`Ably token refresh permanently failing for conversation ${conversationId}`);
    this.name = 'PermanentAuthFailureError';
    this.conversationId = conversationId;
  }
}
export function isPermanentAuthFailure(error) {
  return error instanceof PermanentAuthFailureError;
}
export function createGetTokenFn(pollingClient, getConversationId, options = {}) {
  const {
    retryDelayMs = 1000,
    getAblyAuthToken,
    getIsLocalAgent,
    getLocalAgentId,
    getChannelId
  } = options;
  let consecutiveFailures = 0;
  let breakerConversationId = null;
  return async () => {
    const conversationId = getConversationId();
    if (conversationId === null) {
      throw new Error('Cannot refresh Ably token: no active conversation');
    }
    // Reset the breaker whenever the active conversation changes.
    if (conversationId !== breakerConversationId) {
      breakerConversationId = conversationId;
      consecutiveFailures = 0;
    }
    if (consecutiveFailures >= TOKEN_REFRESH_MAX_CONSECUTIVE_FAILURES) {
      throw new PermanentAuthFailureError(conversationId);
    }
    const refresh = async () => {
      const isLocal = getIsLocalAgent === null || getIsLocalAgent === void 0 ? void 0 : getIsLocalAgent();
      if (isLocal) {
        const agentId = getLocalAgentId === null || getLocalAgentId === void 0 ? void 0 : getLocalAgentId();
        const channelId = getChannelId === null || getChannelId === void 0 ? void 0 : getChannelId();
        if (agentId && channelId) {
          try {
            return await fetchLocalAblyToken(agentId, channelId);
          } catch (_unused2) {
            // fall through to server token
          }
        }
      }
      if (getAblyAuthToken && !isLocal) {
        const result = getAblyAuthToken(conversationId);
        if (result !== null) {
          const authResult = await result.catch(() => null);
          if (authResult !== null) {
            return authResult;
          }
        }
      }
      const fetchToken = async () => {
        const response = await pollingClient.call(getAblyTokenRpc, {
          request: {
            conversationId
          }
        });
        return {
          token: response.token,
          channelId: response.channelId
        };
      };
      try {
        return await fetchToken();
      } catch (_unused3) {
        await new Promise(resolve => setTimeout(resolve, retryDelayMs));
        return await fetchToken();
      }
    };
    try {
      const token = await refresh();
      consecutiveFailures = 0;
      return token;
    } catch (error) {
      consecutiveFailures += 1;
      throw error;
    }
  };
}